import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { normalizeTier } from '../../utils/tier';
import { Button } from '../ui/button';
import {
  Briefcase,
  Building2,
  Users,
  FileText,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Clock,
  DollarSign,
  Globe,
  Eye,
  Settings,
  BarChart3,
  CreditCard,
  Shield
} from 'lucide-react';
import companyParameterService from '../../services/companyParameterService';
import { useAuth } from '@/components/auth/AuthContext';
import { useNavigate } from 'react-router-dom';

interface AdminDashboardNewProps {
  jobs: any[];
  users: any[];
  applications: any[];
  companies: any[];
  stats: any;
  onNavigateToSection?: (section: string) => void;
}

export function AdminDashboardNew({ jobs, users, applications, companies, stats, onNavigateToSection }: AdminDashboardNewProps) {
  const navigate = useNavigate();
  const [industryList, setIndustryList] = useState<string[]>([]);
  const { user } = useAuth();
  const isSuperAdmin = user?.user_type === 'super_admin';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res: any = await companyParameterService.list('industry');
        const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        const names = items.map((it: any) => (typeof it === 'string' ? it : it?.name)).filter(Boolean);
        if (mounted) setIndustryList(names as string[]);
      } catch {
        // If parameters fail to load, keep list empty and allow graceful fallbacks
      }
    })();
    return () => { mounted = false; };
  }, []);
  // Derive dashboard statistics from live data with safe fallbacks
  const isExpired = (j: any) => {
    const raw = (j as any)?.expiryDate || (j as any)?.expiry_date || (j as any)?.deadline;
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;
    return d.getTime() <= Date.now();
  };
  const isActive = (j: any) => String((j as any)?.status || '').toLowerCase() === 'active' && !isExpired(j);
  const megaJobs = jobs.filter(j => normalizeTier(j?.tier) === 'megajob' && isActive(j)).length;
  const premiumJobs = jobs.filter(j => normalizeTier(j?.tier) === 'premium' && isActive(j)).length;
  const primeJobs = jobs.filter(j => normalizeTier(j?.tier) === 'prime' && isActive(j)).length;
  const latestJobs = jobs.filter(isActive).length;
  const newspaperJobs = jobs.filter(j => (normalizeTier(j?.tier) === 'newspaper' || j?.source === 'newspaper') && isActive(j)).length;

  const asLower = (v: any) => String(v || '').toLowerCase();
  const totalAdmins = users.filter(u => {
    const t1 = asLower((u as any)?.type);
    const t2 = asLower((u as any)?.user_type);
    const staff = ['super_admin', 'admin'];
    return staff.includes(t1) || staff.includes(t2);
  }).length;
  const jobSeekerCount = users.filter(u => u?.type === 'jobseeker' || u?.user_type === 'job_seeker').length;
  const employerCount = companies?.length ?? 0; // use companies for employer management
  // Users tile now shows HR, Content, and Support accounts
  const totalUsers = users.filter(u => {
    const t1 = asLower((u as any)?.type);
    const t2 = asLower((u as any)?.user_type);
    const nonAdminStaff = ['hr', 'content', 'content_manager', 'support', 'support_agent'];
    return nonAdminStaff.includes(t1) || nonAdminStaff.includes(t2);
  }).length;
  const totalJobSeekers = jobSeekerCount;
  const totalEmployers = employerCount;

  const verifiedEmployers = companies?.filter(c => !!c?.verified).length ?? 0;
  const pendingEmployers = companies?.filter(c => !c?.verified).length ?? 0;
  const unverifiedUsers = users.filter(u => (u as any)?.isVerified === false).length; // will be 0 if not present
  const verifiedJobSeekers = users.filter(u => u?.type === 'jobseeker' && (u as any)?.isVerified === true).length;
  const unverifiedJobSeekers = users.filter(u => u?.type === 'jobseeker' && (u as any)?.isVerified === false).length;

  // Application statistics
  const totalApplications = applications?.length || 0;
  const pendingApplications = applications?.filter(a => a?.status === 'pending')?.length || 0;
  const reviewedApplications = applications?.filter(a => ['reviewed', 'shortlisted'].includes(a?.status))?.length || 0;
  const acceptedApplications = applications?.filter(a => ['accepted', 'hired'].includes(a?.status))?.length || 0;

  const dashboardStats = {
    megaJobs,
    premiumJobs,
    primeJobs,
    latestJobs,
    newspaperJobs,
    totalAdmins,
    totalUsers: stats?.totalUsers ?? totalUsers,
    totalEmployers: stats?.totalEmployers ?? totalEmployers,
    totalJobSeekers: stats?.totalJobSeekers ?? totalJobSeekers,
    verifiedEmployers: stats?.verifiedEmployers ?? verifiedEmployers,
    pendingEmployers: stats?.pendingEmployers ?? pendingEmployers,
    unverifiedUsers: stats?.totalUnverifiedUsers ?? unverifiedUsers,
    verifiedJobSeekers: stats?.totalVerifiedJobSeekers ?? verifiedJobSeekers,
    unverifiedJobSeekers: stats?.totalUnverifiedJobSeekers ?? unverifiedJobSeekers,
    totalApplications: stats?.totalApplications ?? totalApplications,
    pendingApplications: stats?.pendingApplications ?? pendingApplications,
    reviewedApplications,
    acceptedApplications
  };

  const jobRequests = jobs.slice(0, 5).map((j, idx) => {
    const idMatch = String((j as any)?.company_id || (j as any)?.companyId || '');
    const nameMatch = String((j as any)?.company?.name || (j as any)?.company || '').trim().toLowerCase();
    const byId = (companies || []).find(c => String((c as any)?._id || (c as any)?.id || '') === idMatch);
    const byName = (companies || []).find(c => String((c as any)?.name || (c as any)?.company_name || (c as any)?.title || '').trim().toLowerCase() === nameMatch);
    const companyRef: any = byId || byName || {};
    const phone = companyRef?.phone || companyRef?.company_phone || '';
    return {
      id: (j as any)?.id ?? idx,
      title: (j as any)?.title ?? 'Job Title',
      companyName: (j as any)?.company ?? 'Company',
      officeNumber: phone || 'N/A',
      status: isExpired(j) ? 'expired' : ((j as any)?.approvalStatus ?? (j as any)?.status ?? 'pending'),
      jobType: (j as any)?.employment_type ?? 'Full Time'
    };
  });

  const [jobReqPage, setJobReqPage] = useState(1);
  const [jobReqPerPage, setJobReqPerPage] = useState(10);
  const jobReqTotal = jobRequests.length;
  const jobReqPages = Math.max(1, Math.ceil(jobReqTotal / jobReqPerPage));
  const jobReqStart = Math.max(0, (jobReqPage - 1) * jobReqPerPage);
  const jobReqEnd = Math.min(jobReqTotal, jobReqStart + jobReqPerPage);
  const jobRequestsPaged = jobRequests.slice(jobReqStart, jobReqEnd);

  // Compute industry distribution (top 5 categories)
  const toLower = (s: any) => String(s || '').trim().toLowerCase();
  const industryMap: Record<string, number> = {};
  // Build company lookup by id and name -> industry
  const companyById: Record<string, string> = {};
  const companyByName: Record<string, string> = {};
  for (const c of (companies || [])) {
    const id = String((c as any)?._id || (c as any)?.id || '');
    const nm = toLower((c as any)?.name || (c as any)?.company_name || (c as any)?.title);
    const ind = (c as any)?.industry || (c as any)?.category || '';
    if (id) companyById[id] = ind;
    if (nm) companyByName[nm] = ind;
  }

  const isValidIndustry = (name: string) => {
    if (!name) return false;
    if (industryList.length === 0) return true; // no server list loaded, allow
    return industryList.some(v => toLower(v) === toLower(name));
  };

  for (const j of jobs) {
    const companyName = toLower((j as any)?.company?.name || (j as any)?.company);
    const companyId = String((j as any)?.company_id || (j as any)?.companyId || '');
    let key = '';
    if (companyName && companyByName[companyName]) key = companyByName[companyName];
    else if (companyId && companyById[companyId]) key = companyById[companyId];
    else if ((j as any)?.category && isValidIndustry((j as any)?.category)) key = (j as any)?.category;

    // Only count if we have a real industry name; skip generic/empty
    if (key && isValidIndustry(key)) {
      industryMap[key] = (industryMap[key] ?? 0) + 1;
    }
  }
  const industryJobs = Object.entries(industryMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([industry, jobCount]) => ({ industry, jobCount }));

  const [indPage, setIndPage] = useState(1);
  const [indPerPage, setIndPerPage] = useState(10);
  const indTotal = industryJobs.length;
  const indPages = Math.max(1, Math.ceil(indTotal / indPerPage));
  const indStart = Math.max(0, (indPage - 1) * indPerPage);
  const indEnd = Math.min(indTotal, indStart + indPerPage);
  const industryJobsPaged = industryJobs.slice(indStart, indEnd);

  // Admins by role counts
  // helper already defined above
  const roleCounts = {
    superAdmin: users.filter(u => asLower((u as any)?.user_type) === 'super_admin').length,
    admin: users.filter(u => asLower((u as any)?.user_type) === 'admin').length,
    hrManager: users.filter(u => ['hr', 'hr_manager'].includes(asLower((u as any)?.user_type))).length,
    contentManager: users.filter(u => ['content', 'content_manager'].includes(asLower((u as any)?.user_type))).length,
    supportAgent: users.filter(u => ['support', 'support_agent'].includes(asLower((u as any)?.user_type))).length,
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 text-xs">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 text-xs">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 text-xs">Rejected</Badge>;
      case 'expired':
        return <Badge className="bg-gray-100 text-gray-800 text-xs">Expired</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  const StatCard = ({ title, count, icon: Icon, gradient, textColor, onClick, section }: any) => (
    <Card
      className="relative overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-sm border-l group"
      onClick={() => onClick && onClick(section)}
      style={{ borderLeftColor: '#374151' }}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{title}</p>
            <p className="text-xl font-bold text-gray-800">{count}</p>
          </div>
          <div className="p-2 bg-gray-50 group-hover:bg-gray-100 transition-all duration-200">
            <Icon className="h-5 w-5 text-gray-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Top Statistics Row - Job Types */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard
          title="Mega Jobs"
          count={dashboardStats.megaJobs}
          icon={Briefcase}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=job-post-management&tier=megajob&status=all' });
            onNavigateToSection && onNavigateToSection('job-post-management');
          }}
        />
        <StatCard
          title="Premium Jobs"
          count={dashboardStats.premiumJobs}
          icon={Briefcase}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=job-post-management&tier=premium&status=all' });
            onNavigateToSection && onNavigateToSection('job-post-management');
          }}
        />
        <StatCard
          title="Prime Jobs"
          count={dashboardStats.primeJobs}
          icon={Briefcase}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=job-post-management&tier=prime&status=all' });
            onNavigateToSection && onNavigateToSection('job-post-management');
          }}
        />
        <StatCard
          title="Latest Jobs"
          count={dashboardStats.latestJobs}
          icon={Briefcase}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=job-post-management&tier=latest&status=all' });
            onNavigateToSection && onNavigateToSection('job-post-management');
          }}
        />
        <StatCard
          title="Newspaper Jobs"
          count={dashboardStats.newspaperJobs}
          icon={FileText}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=job-post-management&tier=newspaper&status=all' });
            onNavigateToSection && onNavigateToSection('job-post-management');
          }}
        />
      </div>

      {/* User Statistics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Pending Employers"
          count={dashboardStats.pendingEmployers}
          icon={Building2}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=employer-management&preset=verification-queue' });
            onNavigateToSection && onNavigateToSection('employer-management');
          }}
        />
        <StatCard
          title="Users"
          count={dashboardStats.totalUsers}
          icon={Users}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=user-management&status=all&type=all' });
            onNavigateToSection && onNavigateToSection('user-management');
          }}
        />
        <StatCard
          title="Employers"
          count={dashboardStats.totalEmployers}
          icon={Building2}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=employer-management' });
            onNavigateToSection && onNavigateToSection('employer-management');
          }}
        />
        <StatCard
          title="Job Seekers"
          count={dashboardStats.totalJobSeekers}
          icon={Users}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=user-management&type=jobseeker' });
            onNavigateToSection && onNavigateToSection('user-management');
          }}
        />
      </div>

      {/* Verification Statistics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Verified Employers"
          count={dashboardStats.verifiedEmployers}
          icon={CheckCircle}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=employer-management&preset=verified' });
            onNavigateToSection && onNavigateToSection('employer-management');
          }}
        />
        <StatCard
          title="Unverified Users"
          count={dashboardStats.unverifiedUsers}
          icon={AlertCircle}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=user-management&status=unverified' });
            onNavigateToSection && onNavigateToSection('user-management');
          }}
        />
        <StatCard
          title="Verified Job Seekers"
          count={dashboardStats.verifiedJobSeekers}
          icon={CheckCircle}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=user-management&status=verified&type=jobseeker' });
            onNavigateToSection && onNavigateToSection('user-management');
          }}
        />
        <StatCard
          title="Unverified Job Seekers"
          count={dashboardStats.unverifiedJobSeekers}
          icon={AlertCircle}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={() => {
            navigate({ search: '?section=user-management&status=unverified&type=jobseeker' });
            onNavigateToSection && onNavigateToSection('user-management');
          }}
        />
      </div>

      {/* Applications Statistics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total Applications"
          count={dashboardStats.totalApplications}
          icon={FileText}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={onNavigateToSection}
          section="applications-management"
        />
        <StatCard
          title="Pending Applications"
          count={dashboardStats.pendingApplications}
          icon={Clock}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={onNavigateToSection}
          section="applications-management"
        />
        <StatCard
          title="Reviewed/Shortlisted"
          count={dashboardStats.reviewedApplications}
          icon={Eye}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={onNavigateToSection}
          section="applications-management"
        />
        <StatCard
          title="Accepted/Hired"
          count={dashboardStats.acceptedApplications}
          icon={CheckCircle}
          gradient="bg-gray-50"
          textColor="text-gray-800"
          onClick={onNavigateToSection}
          section="applications-management"
        />
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Job Requests Table */}
        <Card className="h-full">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">Job Requests</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigateToSection && onNavigateToSection('job-post-management')}
                className="hover:bg-gray-50 hover:text-gray-800 transition-colors duration-200"
              >
                <Eye className="h-4 w-4 mr-2" />
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-2">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-xs text-gray-500 uppercase">
                    <th className="text-left py-1 px-1 w-8">#</th>
                    <th className="text-left py-1 px-1">Title</th>
                    <th className="text-left py-1 px-1 hidden sm:table-cell">Company Name</th>
                    <th className="text-left py-1 px-1 hidden md:table-cell">Office Number</th>
                    <th className="text-left py-1 px-1">Status</th>
                    <th className="text-left py-1 px-1 hidden sm:table-cell">Job Type</th>
                    <th className="text-left py-1 px-1">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {jobRequestsPaged.map((request, index) => (
                    <tr key={request.id} className="border-b hover:bg-gray-50">
                      <td className="py-1 px-1 text-xs">{jobReqStart + index + 1}</td>
                      <td className="py-1 px-1 text-xs font-medium">
                        <div className="max-w-[150px] truncate" title={request.title}>{request.title}</div>
                        <div className="sm:hidden text-xs text-gray-500 mt-1">{request.companyName}</div>
                      </td>
                      <td className="py-1 px-1 text-xs hidden sm:table-cell">{request.companyName}</td>
                      <td className="py-1 px-1 text-xs hidden md:table-cell">{request.officeNumber}</td>
                      <td className="py-1 px-1">{getStatusBadge(request.status)}</td>
                      <td className="py-1 px-1 text-xs hidden sm:table-cell">{request.jobType}</td>
                      <td className="py-1 px-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs hover:bg-gray-50 hover:text-gray-800 transition-colors duration-200"
                          onClick={() => onNavigateToSection && onNavigateToSection('job-post-management')}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-xs text-gray-600">
                Showing {jobReqTotal === 0 ? 0 : jobReqStart + 1} to {jobReqEnd} of {jobReqTotal} entries
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">Show</label>
                <select
                  className="border rounded h-7 text-xs px-2"
                  value={jobReqPerPage}
                  onChange={(e) => { setJobReqPerPage(Number(e.target.value)); setJobReqPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={jobReqPage <= 1} onClick={() => setJobReqPage(p => Math.max(1, p - 1))}>Previous</Button>
                <span className="text-xs text-gray-600">{jobReqPage} / {jobReqPages}</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={jobReqPage >= jobReqPages} onClick={() => setJobReqPage(p => Math.min(jobReqPages, p + 1))}>Next</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Industry Jobs Table */}
        <Card className="h-full">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">Industry Jobs</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigateToSection && onNavigateToSection('reports')}
                className="hover:bg-gray-50 hover:text-gray-800 transition-colors duration-200"
              >
                <Eye className="h-4 w-4 mr-2" />
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-2">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-xs text-gray-500 uppercase">
                    <th className="text-left py-1 px-1 w-8">#</th>
                    <th className="text-left py-1 px-1">Industry</th>
                    <th className="text-left py-1 px-1 text-center">Job Count</th>
                  </tr>
                </thead>
                <tbody>
                  {industryJobsPaged.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-1 px-1 text-xs">{indStart + index + 1}</td>
                      <td className="py-1 px-1 text-xs font-medium">{item.industry}</td>
                      <td className="py-1 px-1 text-center">
                        <Badge variant="outline" className="text-xs">
                          {item.jobCount}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
          <CardFooter className="pt-0 px-2 pb-2 mt-auto">
            <div className="flex items-center justify-between w-full">
              <div className="text-xs text-gray-600">
                Showing {indTotal === 0 ? 0 : indStart + 1} to {indEnd} of {indTotal} entries
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">Show</label>
                <select
                  className="border rounded h-7 text-xs px-2"
                  value={indPerPage}
                  onChange={(e) => { setIndPerPage(Number(e.target.value)); setIndPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={indPage <= 1} onClick={() => setIndPage(p => Math.max(1, p - 1))}>Previous</Button>
                <span className="text-xs text-gray-600">{indPage} / {indPages}</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={indPage >= indPages} onClick={() => setIndPage(p => Math.min(indPages, p + 1))}>Next</Button>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>

      {/* Super Admins by Role - visible only to Super Admin */}
      {isSuperAdmin && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text_base font-medium">Super Admins by Role</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigateToSection && onNavigateToSection('user-management')}
                className="hover:bg-gray-50 hover:text-gray-800 transition-colors duration-200"
              >
                <Eye className="h-4 w-4 mr-2" />
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Super Admin</span>
                </div>
                <Badge variant="outline" className="text-xs">{roleCounts.superAdmin}</Badge>
              </div>
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-600" />
                  <span className="text-sm">Admin</span>
                </div>
                <Badge variant="outline" className="text-xs">{roleCounts.admin}</Badge>
              </div>
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-600" />
                  <span className="text-sm">HR Manager</span>
                </div>
                <Badge variant="outline" className="text-xs">{roleCounts.hrManager}</Badge>
              </div>
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm">Content Manager</span>
                </div>
                <Badge variant="outline" className="text-xs">{roleCounts.contentManager}</Badge>
              </div>
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-red-600" />
                  <span className="text-sm">Support Agent</span>
                </div>
                <Badge variant="outline" className="text-xs">{roleCounts.supportAgent}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <Button
              variant="outline"
              className="h-14 flex-col hover:bg-gray-50 hover:text-gray-800 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group"
              onClick={() => onNavigateToSection && onNavigateToSection('job-post-management')}
            >
              <Briefcase className="h-5 w-5 mb-1 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-xs">Approve Jobs</span>
            </Button>
            <Button
              variant="outline"
              className="h-14 flex-col hover:bg-gray-50 hover:text-gray-800 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group"
              onClick={() => onNavigateToSection && onNavigateToSection('user-management')}
            >
              <Users className="h-5 w-5 mb-1 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-xs">Verify Users</span>
            </Button>
            <Button
              variant="outline"
              className="h-14 flex-col hover:bg-gray-50 hover:text-gray-800 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group"
              onClick={() => onNavigateToSection && onNavigateToSection('employer-management')}
            >
              <Building2 className="h-5 w-5 mb-1 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-xs">Manage Companies</span>
            </Button>
            <Button
              variant="outline"
              className="h-14 flex-col hover:bg-gray-50 hover:text-gray-800 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group"
              onClick={() => onNavigateToSection && onNavigateToSection('reports')}
            >
              <BarChart3 className="h-5 w-5 mb-1 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-xs">Generate Report</span>
            </Button>
            <Button
              variant="outline"
              className="h-14 flex-col hover:bg-gray-50 hover:text-gray-800 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group"
              onClick={() => onNavigateToSection && onNavigateToSection('payment-management')}
            >
              <CreditCard className="h-5 w-5 mb-1 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-xs">Payments</span>
            </Button>
            <Button
              variant="outline"
              className="h-14 flex-col hover:bg-gray-50 hover:text-gray-800 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group"
              onClick={() => onNavigateToSection && onNavigateToSection('role-management')}
            >
              <Shield className="h-5 w-5 mb-1 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-xs">Role Management</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
